import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../context/AuthContext';
import { Dice5, Trophy, Clock, Users, Loader2, Check, Lock, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const PickNumberGame = () => {
  const { currentUser } = useAuth();

  const [currentGame, setCurrentGame] = useState(null);
  const [myEntry, setMyEntry] = useState(null);
  const [gameHistory, setGameHistory] = useState([]);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [availableNumbers, setAvailableNumbers] = useState([]);

  useEffect(() => {
    const fetchGameData = async () => {
      try {
        setLoading(true);
        const gameRes = await api.get('/api/pick-number-game/current');
        setCurrentGame(gameRes.data);

        if (gameRes.data && currentUser) {
          const entryRes = await api.get(`/api/pick-number-game/my-entry/${gameRes.data.id}`);
          setMyEntry(entryRes.data);

          const availRes = await api.get(`/api/pick-number-game/available-numbers/${gameRes.data.id}`);
          setAvailableNumbers(availRes.data.available_numbers);
        }

        const historyRes = await api.get('/api/pick-number-game/history?limit=5');
        setGameHistory(historyRes.data);
      } catch (error) {
        console.error('Error fetching game data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGameData();
  }, [currentUser]);

  const handlePickNumber = async () => {
    if (!selectedNumber) {
      toast.error('Please select a number first');
      return;
    }

    setSubmitting(true);

    try {
      // Self-heal: re-fetch current game in case the cached one is stale (PWA cache)
      let gameId = currentGame?.id;
      try {
        const freshGame = await api.get('/api/pick-number-game/current');
        if (freshGame.data?.id) {
          gameId = freshGame.data.id;
          if (gameId !== currentGame?.id) {
            setCurrentGame(freshGame.data);
          }
        }
      } catch (e) {
        // fall back to cached
      }

      if (!gameId) {
        toast.error('No active game right now. Pull down to refresh.');
        setSubmitting(false);
        return;
      }

      const response = await api.post('/api/pick-number-game/pick', {
        game_id: gameId,
        chosen_number: selectedNumber,
      });

      toast.success(response.data.message, { duration: 5000 });

      const [entryRes, availRes] = await Promise.all([
        api.get(`/api/pick-number-game/my-entry/${gameId}`),
        api.get(`/api/pick-number-game/available-numbers/${gameId}`),
      ]);
      setMyEntry(entryRes.data);
      setAvailableNumbers(availRes.data.available_numbers);
    } catch (error) {
      console.error('Error picking number:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to pick number';
      // Translate generic "Not Found" into something actionable
      const friendly = errorMsg === 'Not Found'
        ? 'Service unavailable. Close the app, reopen it, and try again.'
        : errorMsg;
      toast.error(friendly);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="sauce-sity-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 size={48} className="animate-spin" style={{ color: 'var(--ss-green)' }} />
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="sauce-sity-page" data-testid="game-page-guest">
        <div className="sauce-sity-content" style={{ textAlign: 'center', paddingTop: 80 }}>
          <Dice5 size={80} style={{ margin: '0 auto', color: 'var(--ss-green)' }} />
          <h1 className="ss-title-xl ss-mt-24">Pick a <span className="ss-highlight-purple">Number</span> Game</h1>
          <p className="ss-text ss-mb-32">Sign in to play and win prizes</p>
          <Link to="/login">
            <button className="ss-btn ss-btn-primary ss-btn-lg" data-testid="game-signin-btn">Sign In to Play</button>
          </Link>
        </div>
      </div>
    );
  }

  const gameEnded = currentGame && new Date(currentGame.end_date) < new Date();
  const canPlay = currentGame && !myEntry && !gameEnded;

  return (
    <div className="sauce-sity-page" data-testid="game-page">
      <div className="sauce-sity-content">
        <div className="ss-section-header ss-mb-48">
          <h1 className="ss-title-xl">
            <span className="ss-highlight-green">Pick a Number</span> Game
          </h1>
          <p className="ss-section-subtitle">Weekly contest — pick your lucky number and win prizes!</p>
        </div>

        {currentGame ? (
          <div className="ss-card ss-card-outline-green ss-mb-48" data-testid="current-game-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 className="ss-title-lg" style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
                <Trophy size={24} style={{ color: 'var(--ss-green)' }} />
                Game #{currentGame.game_number}
              </h2>
              <span style={{
                background: gameEnded ? 'rgba(255,255,255,0.06)' : 'var(--ss-green)',
                color: gameEnded ? 'var(--ss-white-soft)' : '#111',
                padding: '6px 12px',
                borderRadius: 999,
                fontWeight: 800,
                fontSize: '0.78rem',
              }}>
                {gameEnded ? 'Ended' : 'Active'}
              </span>
            </div>

            <div className="ss-grid-3 ss-mb-24">
              <div className="ss-card ss-card-dark" style={{ padding: 16 }}>
                <div style={{ color: 'var(--ss-white-soft)', fontSize: '0.85rem', display: 'flex', gap: 6, alignItems: 'center' }}>
                  <Trophy size={14} /> Prize
                </div>
                <p style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--ss-green)', margin: '4px 0 0' }}>{currentGame.prize}</p>
              </div>
              <div className="ss-card ss-card-dark" style={{ padding: 16 }}>
                <div style={{ color: 'var(--ss-white-soft)', fontSize: '0.85rem', display: 'flex', gap: 6, alignItems: 'center' }}>
                  <Clock size={14} /> Ends
                </div>
                <p style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--ss-white)', margin: '4px 0 0' }}>
                  {new Date(currentGame.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </p>
              </div>
              <div className="ss-card ss-card-dark" style={{ padding: 16 }}>
                <div style={{ color: 'var(--ss-white-soft)', fontSize: '0.85rem', display: 'flex', gap: 6, alignItems: 'center' }}>
                  <Users size={14} /> Entries
                </div>
                <p style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--ss-purple)', margin: '4px 0 0' }}>{currentGame.total_entries}</p>
              </div>
            </div>

            {myEntry ? (
              <div className="ss-card ss-card-outline-green ss-text-center" data-testid="my-entry">
                <Check size={48} style={{ color: 'var(--ss-green)', margin: '0 auto 8px' }} />
                <h3 className="ss-title-lg">You're In!</h3>
                <p className="ss-text">Your lucky number:</p>
                <div style={{
                  display: 'inline-block',
                  background: 'var(--ss-green)',
                  color: '#111',
                  fontWeight: 800,
                  fontSize: '2.4rem',
                  borderRadius: 16,
                  padding: '16px 32px',
                  marginTop: 8,
                }}>
                  {myEntry.chosen_number}
                </div>
                <p className="ss-text ss-mt-24" style={{ fontSize: '0.85rem' }}>
                  Good luck! Winner announced on {new Date(currentGame.end_date).toLocaleDateString()}
                </p>
              </div>
            ) : canPlay ? (
              <div>
                <h3 className="ss-title-md ss-mb-16">Pick Your Lucky Number (1-100)</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: 6 }}>
                  {Array.from({ length: 100 }, (_, i) => i + 1).map((num) => {
                    const isAvailable = availableNumbers.includes(num);
                    const isSelected = selectedNumber === num;
                    return (
                      <button
                        key={num}
                        onClick={() => isAvailable && setSelectedNumber(num)}
                        disabled={!isAvailable}
                        data-testid={`num-${num}`}
                        style={{
                          aspectRatio: '1 / 1',
                          borderRadius: 10,
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          border: isSelected ? '2px solid var(--ss-green)' : '1px solid var(--ss-border)',
                          background: isSelected ? 'var(--ss-green)' : isAvailable ? 'rgba(20,20,20,0.88)' : 'rgba(10,10,10,0.64)',
                          color: isSelected ? '#111' : isAvailable ? 'var(--ss-white)' : 'var(--ss-white-soft)',
                          cursor: isAvailable ? 'pointer' : 'not-allowed',
                          opacity: isAvailable ? 1 : 0.4,
                          transform: isSelected ? 'scale(1.08)' : 'scale(1)',
                          transition: 'transform 0.15s ease, background 0.15s ease',
                        }}
                      >
                        {isAvailable ? num : <Lock size={12} style={{ margin: '0 auto' }} />}
                      </button>
                    );
                  })}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, flexWrap: 'wrap', gap: 12 }}>
                  <p className="ss-text" style={{ fontSize: '0.85rem' }}>
                    {availableNumbers.length} numbers available • {100 - availableNumbers.length} taken
                  </p>
                  <button
                    onClick={handlePickNumber}
                    disabled={!selectedNumber || submitting}
                    data-testid="lock-number-btn"
                    className="ss-btn ss-btn-primary"
                    style={{ opacity: (!selectedNumber || submitting) ? 0.6 : 1, cursor: (!selectedNumber || submitting) ? 'not-allowed' : 'pointer' }}
                  >
                    {submitting
                      ? <><Loader2 size={16} className="animate-spin" /> Submitting…</>
                      : <><Check size={16} /> Lock In Number</>}
                  </button>
                </div>
              </div>
            ) : (
              <div className="ss-card ss-card-dark ss-text-center" style={{ padding: 32 }}>
                <Clock size={48} style={{ color: 'var(--ss-white-soft)', margin: '0 auto 12px' }} />
                <h3 className="ss-title-md">Game Ended</h3>
                <p className="ss-text">Check back soon for the next game!</p>
              </div>
            )}
          </div>
        ) : (
          <div className="ss-card ss-card-purple ss-text-center ss-mb-48" style={{ padding: 48 }} data-testid="no-active-game">
            <Dice5 size={64} style={{ color: 'var(--ss-white-soft)', margin: '0 auto 12px' }} />
            <h3 className="ss-title-lg">No Active Game</h3>
            <p className="ss-text">Check back soon for the next weekly contest!</p>
          </div>
        )}

        {/* How to Play */}
        <div className="ss-card ss-mb-48" data-testid="how-to-play">
          <h2 className="ss-title-lg ss-mb-24">How to <span className="ss-highlight-green">Play</span></h2>
          <div className="ss-grid-3">
            {[
              { step: '1', title: 'Pick a Number', desc: 'Choose any number from 1-100 that\'s not already taken', color: 'var(--ss-green)' },
              { step: '2', title: 'Wait for Draw', desc: 'Game runs weekly, winner announced at end date', color: 'var(--ss-purple)' },
              { step: '3', title: 'Closest Wins!', desc: 'The number closest to the winning draw wins the prize', color: 'var(--ss-green)' },
            ].map((item, i) => (
              <div key={i} className="ss-text-center">
                <div style={{
                  width: 64, height: 64,
                  borderRadius: '999px',
                  background: `${item.color === 'var(--ss-green)' ? 'rgba(170,255,0,0.14)' : 'rgba(161,0,255,0.18)'}`,
                  display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.6rem',
                  fontWeight: 800,
                  color: item.color,
                  marginBottom: 12,
                }}>{item.step}</div>
                <h3 className="ss-title-md">{item.title}</h3>
                <p className="ss-text" style={{ fontSize: '0.9rem' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {gameHistory.length > 0 && (
          <div className="ss-card" data-testid="past-winners">
            <h2 className="ss-title-lg ss-mb-24" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Crown size={22} style={{ color: 'var(--ss-green)' }} /> Past Winners
            </h2>
            <div>
              {gameHistory.map((game) => (
                <div
                  key={game.id}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderBottom: '1px solid var(--ss-border)' }}
                >
                  <div>
                    <p style={{ fontWeight: 700, color: 'var(--ss-white)', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Trophy size={16} style={{ color: 'var(--ss-green)' }} /> Game #{game.game_number} — {game.prize}
                    </p>
                    <p style={{ color: 'var(--ss-white-soft)', fontSize: '0.8rem', margin: 0 }}>
                      {new Date(game.end_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontWeight: 700, color: 'var(--ss-green)', margin: 0 }}>{game.winner_name}</p>
                    <p style={{ color: 'var(--ss-white-soft)', fontSize: '0.8rem', margin: 0 }}>Winning #: {game.winning_number}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PickNumberGame;
